import { useState } from "react";
import { useSearchParams } from "react-router";
import { Send } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { EmptyState, InfoHint, PageHeader, Skeleton, ToggleGroup, ToggleGroupItem } from "@ncp/ui";
import { OPEN_STATUSES, useRequests, type RequestKind } from "./api";
import { RequestTable } from "./RequestTable";
import { RequestReviewSheet } from "./RequestReviewSheet";

/** Every request the Department has sent outside its files – market-study data and technical opinions – with what is still awaited. */
export function RequestsRegisterPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [kind, setKind] = useState<RequestKind | "all">("all");
  const [openOnly, setOpenOnly] = useState(true);
  const requests = useRequests({ kind: kind === "all" ? null : kind, open: openOnly });
  const shown = params.get("request");
  const open = (id: string | null) => setParams((p) => { const next = new URLSearchParams(p); if (id) next.set("request", id); else next.delete("request"); return next; }, { replace: true });
  const rows = requests.data ?? [];
  const waiting = rows.filter((r) => r.status === "Submitted").length;
  const overdue = rows.filter((r) => r.overdue && OPEN_STATUSES.includes(r.status)).length;
  return (
    <div>
      <PageHeader title={<span className="inline-flex items-center gap-2">{t("backoffice:requests.title")}<InfoHint>{t("backoffice:requests.subtitle")}</InfoHint></span>} />
      {/* Filters, not tabs: a segmented control for the kind of request and one for whether closed requests are listed. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ToggleGroup type="single" size="sm" value={kind} onValueChange={(v) => { if (v) setKind(v as RequestKind | "all"); }} aria-label={t("backoffice:requests.title")}>
          <ToggleGroupItem value="all">{t("backoffice:requests.filters.all")}</ToggleGroupItem>
          <ToggleGroupItem value="MarketData">{t("backoffice:requests.kind.MarketData")}</ToggleGroupItem>
          <ToggleGroupItem value="TechnicalOpinion">{t("backoffice:requests.kind.TechnicalOpinion")}</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup type="single" size="sm" value={openOnly ? "open" : "all"} onValueChange={(v) => { if (v) setOpenOnly(v === "open"); }} aria-label={t("backoffice:requests.filters.open")}>
          <ToggleGroupItem value="open">{t("backoffice:requests.filters.open")}</ToggleGroupItem>
          <ToggleGroupItem value="all">{t("backoffice:requests.filters.withClosed")}</ToggleGroupItem>
        </ToggleGroup>
        <span className="ms-auto text-xs text-muted-foreground">
          {waiting > 0 ? `${t("backoffice:requests.status.Submitted")}: ${waiting}` : null}
          {waiting > 0 && overdue > 0 ? " · " : null}
          {overdue > 0 ? `${t("backoffice:requests.overdue")}: ${overdue}` : null}
        </span>
      </div>
      {requests.isLoading ? <Skeleton className="h-64" /> : rows.length === 0
        ? <EmptyState icon={Send} title={t("backoffice:requests.empty")} description={t("backoffice:requests.emptyHint")} />
        : <RequestTable rows={rows} onOpen={open} showAbout />}
      <RequestReviewSheet id={shown} onClose={() => open(null)} />
    </div>
  );
}
